import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import { useCourierOffices, useCouriers } from "@/api/courier-query";
import { useCities, useCountries, useStates } from "@/api/util";
import DiscoveryFooter from "@/components/index/discovery-footer";
import NavbarMinimal from "@/components/index/navbar-minimal";
import { Button } from "@/components/ui/button";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";

interface ShippingFormData {
	stateId: string;
	cityId: string;
	courierId: string;
	courierOfficeId: string;
	address: string;
	phoneNumber: string;
	countryCode: string;
}

function ShippingAddressPage() {
	const navigate = useNavigate();
	const search = useSearch({ from: "/checkout/shipping" });
	const bagId = search.bagId || "";

	const [formData, setFormData] = useState<ShippingFormData>({
		stateId: "",
		cityId: "",
		courierId: "",
		courierOfficeId: "",
		address: "",
		phoneNumber: "",
		countryCode: "+234"
	});

	// Fetch countries to get Nigeria's ID
	const { data: countriesData } = useCountries();
	const nigeriaCountry = countriesData?.data?.find(
		country => country.name === "Nigeria" || country.iso2 === "NG" || country.iso3 === "NGA"
	);
	const NIGERIA_COUNTRY_ID = nigeriaCountry?.id || "";

	// Fetch ALL courier offices for Nigeria — drives which states/cities are available
	const { data: allOfficesData, isLoading: officesLoading } = useCourierOffices({
		countryId: NIGERIA_COUNTRY_ID || undefined,
		limit: 1000
	});
	const allOffices = useMemo(() => {
		const data = allOfficesData?.data;
		if (Array.isArray(data))
			return data;
		return (data as any)?.data || [];
	}, [allOfficesData]);

	// Collect stateIds and cityIds that have courier offices
	const officeStateIds = useMemo(() => {
		const ids = new Set<string>();
		for (const office of allOffices) {
			if (office.stateId)
				ids.add(office.stateId);
		}
		return ids;
	}, [allOffices]);

	const officeCityIds = useMemo(() => {
		const ids = new Set<string>();
		for (const office of allOffices) {
			if (office.stateId === formData.stateId && office.cityId) {
				ids.add(office.cityId);
			}
		}
		return ids;
	}, [allOffices, formData.stateId]);

	// Fetch states from country API, then filter to only those with courier offices
	const { data: statesData, isLoading: statesLoading } = useStates(NIGERIA_COUNTRY_ID);
	const states = useMemo(() => {
		const allStates = statesData || [];
		if (officeStateIds.size === 0)
			return allStates;
		return allStates.filter(s => officeStateIds.has(s.id));
	}, [statesData, officeStateIds]);

	// Fetch cities from country API, then filter to only those with courier offices
	const { data: citiesData } = useCities({
		countryId: NIGERIA_COUNTRY_ID,
		stateId: formData.stateId
	});
	const cities = useMemo(() => {
		const allCities = citiesData || [];
		if (officeCityIds.size === 0)
			return allCities;
		return allCities.filter(c => officeCityIds.has(c.id));
	}, [citiesData, officeCityIds]);

	// Fetch couriers
	const { data: couriersData } = useCouriers();
	const couriers = couriersData?.data?.items || [];

	// Filter courier offices for selected state, city, and courier
	const courierOffices = useMemo(() => {
		return allOffices.filter((office: any) => {
			if (formData.stateId && office.stateId !== formData.stateId)
				return false;
			if (formData.cityId && office.cityId !== formData.cityId)
				return false;
			if (formData.courierId && office.courier?.id !== formData.courierId)
				return false;
			return true;
		});
	}, [allOffices, formData.stateId, formData.cityId, formData.courierId]);

	const handleInputChange = (field: keyof ShippingFormData, value: string) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validation
		if (!formData.stateId || !formData.cityId || !formData.courierId
			|| !formData.courierOfficeId || !formData.address || !formData.phoneNumber) {
			toast.error("Please fill all required fields");
			return;
		}

		if (!bagId) {
			toast.error("Bag ID is missing");
			return;
		}

		// Store shipping data and navigate to summary immediately
		const selectedState = states.find(s => s.id === formData.stateId);
		const selectedCity = cities.find(c => c.id === formData.cityId);

		sessionStorage.setItem("pendingShippingData", JSON.stringify({
			bagId,
			shippingAddress: {
				stateId: formData.stateId,
				cityId: formData.cityId,
				courierId: formData.courierId,
				courierOfficeId: formData.courierOfficeId,
				address: formData.address,
				phoneNumber: `${formData.countryCode}${formData.phoneNumber}`
			},
			buyerAddress: {
				address: formData.address,
				city: selectedCity?.name || "",
				state: selectedState?.name || ""
			}
		}));

		navigate({ to: `/checkout/summary` });
	};

	return (
		<div className="bg-gray-50 min-h-screen flex flex-col">
			<NavbarMinimal />

			{/* Back button: returns to previous page */}
			<div className="flex items-center mb-2">
				<button
					type="button"
					aria-label="Go back"
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{ position: "relative", lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, marginLeft: 12, marginTop: 12 }}
					onClick={() => window.history.back()}
				>
					<img
						src="/assets/icons/arrow-left.svg"
						alt="Back"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
			</div>

			<div className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
				{/* Title */}
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-[#2A2A2A]">Shipping Address</h1>
				</div>

				{/* Shipping Form */}
				<div className="bg-white rounded-lg shadow p-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Pick up state */}
						<div>
							<label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
								Pick up state
							</label>
							<div className="relative">
								<select
									id="state"
									value={formData.stateId}
									onChange={(e) => {
										handleInputChange("stateId", e.target.value);
										// Reset downstream fields when state changes
										handleInputChange("cityId", "");
										handleInputChange("courierId", "");
										handleInputChange("courierOfficeId", "");
										handleInputChange("address", "");
									}}
									className="w-full appearance-none px-4 py-3 pr-10 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8] bg-white"
									required
									disabled={officesLoading || statesLoading}
								>
									<option value="">{(officesLoading || statesLoading) ? "Loading..." : "Select state"}</option>
									{states.map(state => (
										<option key={state.id} value={state.id}>
											{state.name}
										</option>
									))}
								</select>
								<img src="/assets/icons/arrow-down.svg" alt="" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 [filter:brightness(0)_saturate(100%)_invert(40%)]" />
							</div>
						</div>

						{/* City */}
						<div>
							<label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
								City
							</label>
							<div className="relative">
								<select
									id="city"
									value={formData.cityId}
									onChange={e => handleInputChange("cityId", e.target.value)}
									className="w-full appearance-none px-4 py-3 pr-10 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8] bg-white"
									required
									disabled={!formData.stateId}
								>
									<option value="">Select city</option>
									{cities.map(city => (
										<option key={city.id} value={city.id}>
											{city.name}
										</option>
									))}
								</select>
								<img src="/assets/icons/arrow-down.svg" alt="" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 [filter:brightness(0)_saturate(100%)_invert(40%)]" />
							</div>
						</div>

						{/* Pick up courier */}
						<div>
							<label htmlFor="courier" className="block text-sm font-medium text-gray-700 mb-2">
								Pick up courier
							</label>
							<div className="relative">
								<select
									id="courier"
									value={formData.courierId}
									onChange={e => handleInputChange("courierId", e.target.value)}
									className="w-full appearance-none px-4 py-3 pr-10 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8] bg-white"
									required
								>
									<option value="">Select courier</option>
									{couriers.map((courier: any) => (
										<option key={courier.id} value={courier.id}>
											{courier.name || courier.title}
										</option>
									))}
								</select>
								<img src="/assets/icons/arrow-down.svg" alt="" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 [filter:brightness(0)_saturate(100%)_invert(40%)]" />
							</div>
						</div>

						{/* Pick up courier office */}
						<div>
							<label htmlFor="courierOffice" className="block text-sm font-medium text-gray-700 mb-2">
								Pick up courier office
							</label>
							<div className="relative">
								<select
									id="courierOffice"
									value={formData.courierOfficeId}
									onChange={(e) => {
										const selectedOfficeId = e.target.value;
										handleInputChange("courierOfficeId", selectedOfficeId);

										// Auto-fill address from selected courier office immediately
										const selectedOffice = courierOffices.find((office: any) => String(office.id) === selectedOfficeId);
										if (selectedOffice?.address) {
											handleInputChange("address", selectedOffice.address);
										}
										else {
											console.warn("Office not found. Office ID:", selectedOfficeId, "Available offices:", courierOffices);
										}
									}}
									className="w-full appearance-none px-4 py-3 pr-10 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8] bg-white"
									required
									disabled={!formData.courierId}
								>
									<option value="">Select courier office</option>
									{courierOffices.map((office: any) => (
										<option key={office.id} value={String(office.id)}>
											{office.name || office.address}
										</option>
									))}
								</select>
								<img src="/assets/icons/arrow-down.svg" alt="" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 [filter:brightness(0)_saturate(100%)_invert(40%)]" />
							</div>
						</div>

						{/* Address - Auto-filled from courier office */}
						<div>
							<label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
								Courier office address
							</label>
							<input
								id="address"
								type="text"
								value={formData.address}
								onChange={e => handleInputChange("address", e.target.value)}
								placeholder="Select a courier office to auto-fill address"
								className="w-full px-4 py-3 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8] bg-gray-100"
								required
								readOnly
							/>
						</div>

						{/* Phone number */}
						<div>
							<label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
								Phone number
							</label>
							<div className="flex gap-2">
								<div className="flex items-center px-3 py-3 border border-[#B3B3B3] rounded-md bg-gray-50 text-sm text-gray-700">
									+234
								</div>
								<input
									id="phone"
									type="tel"
									value={formData.phoneNumber}
									onChange={e => handleInputChange("phoneNumber", e.target.value)}
									placeholder="80 0000 0000"
									className="flex-1 px-4 py-3 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8]"
									required
								/>
							</div>
						</div>

						{/* Submit Button */}
						<Button
							type="submit"
							className="w-full bg-[#3F2EC8] hover:bg-[#2F1EA8] text-white py-3"
						>
							Continue
						</Button>
					</form>
				</div>
			</div>

			<DiscoveryFooter />
		</div>
	);
}

export const Route = createFileRoute("/checkout/shipping")({
	component: ShippingAddressPage,
	validateSearch: (search: Record<string, unknown>): { bagId?: string } => {
		return {
			bagId: (search.bagId as string) || undefined
		};
	}
});
